import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, Image, ButtonGroup, Button, IconCloseOutline, IconMenuOutline, TextButton } from "@zillow/constellation";
import { yieldCallback } from "@zillow/yield-callback";
import { useEffect } from "react";
import { B2B_MAIN_NAV_BORDER_WIDTH_PX, B2B_MAIN_NAV_PADDING_VERTICAL_PX, B2B_MAIN_NAV_LOGO_MAX_HEIGHT_PX } from "../../constants/stickyNav.esm.js";
import { GROUP_WIDTH_MAP } from "../../mixins/components/GroupMixin.esm.js";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import { usePageProps } from "../PagePropsProvider/PagePropsProvider.esm.js";
import { sendEvent } from "./B2BMainNav.utils.esm.js";
import MenuItem from "./MenuItem.esm.js";
import MenuList from "./MenuList.esm.js";
const TextButtonAnchor = (props) => /* @__PURE__ */ jsx(TextButton, { ...props, as: "a" });
const B2BMainNav = (props) => {
  const {
    menu,
    mobileMenu,
    setMobileMenu,
    primaryCTALink,
    primaryCTALabel,
    secondaryCTALink,
    secondaryCTALabel,
    logo,
    disabled,
    transparent,
    secondaryCTAModal,
    sticky
  } = props;
  const MobileMenuIcon = mobileMenu ? IconCloseOutline : IconMenuOutline;
  function parseMenu(items, root = 0) {
    if (!items || !Array.isArray(items)) {
      return [];
    }
    return items.filter((item) => item.parent == root).map((menuItem) => {
      var _a, _b;
      return {
        id: menuItem.id,
        label: ((_a = menuItem == null ? void 0 : menuItem.title) == null ? void 0 : _a.raw) || ((_b = menuItem == null ? void 0 : menuItem.title) == null ? void 0 : _b.rendered),
        current: false,
        // No router context, so always false
        url: menuItem.url,
        target: menuItem.target,
        children: parseMenu(items.filter((child) => {
          var _a2;
          return ((_a2 = child == null ? void 0 : child.parent) == null ? void 0 : _a2.toString()) == menuItem.id;
        }), menuItem.id),
        ...menuItem.columns && {
          columns: menuItem.columns
        }
      };
    });
  }
  const menuItems = parseMenu(menu);
  const pageProps = usePageProps();
  const cmsInfo = getCmsInfoForPage(pageProps);
  const handleCTAClick = (triggerSource, label, url) => {
    sendEvent(triggerSource, label, url, cmsInfo);
  };
  const LogoContainer = ({
    children,
    ...props2
  }) => {
    if ((logo == null ? void 0 : logo.link) && !disabled) {
      return /* @__PURE__ */ jsx("a", { ...props2, children });
    }
    return /* @__PURE__ */ jsx(Fragment, { children });
  };
  const hasPrimaryCta = primaryCTALink && primaryCTALabel;
  const hasSecondaryCta = secondaryCTALink && secondaryCTALabel || secondaryCTAModal;
  useEffect(() => {
    if (mobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenu]);
  let mobilePosition = "relative";
  let mobileTop = "0";
  if (sticky) {
    mobilePosition = "sticky";
  } else if (mobileMenu) {
    mobilePosition = "absolute";
    mobileTop = "41px";
  }
  return (
    // Box to add border and background, allows to stretch to full width.
    /* @__PURE__ */ jsx(Box, { role: "navigation", "aria-label": "Main Navigation", sx: {
      zIndex: "100",
      position: sticky ? "fixed" : "relative",
      ...sticky && {
        width: "100%"
      },
      top: 0,
      borderBottom: transparent ? `${B2B_MAIN_NAV_BORDER_WIDTH_PX}px solid $colors.white` : `${B2B_MAIN_NAV_BORDER_WIDTH_PX}px solid $colors.borderLight`,
      background: transparent ? "rgba(0, 0, 0, 0)" : "$colors.backgroundWhite",
      "@media $lg_lte": {
        position: mobilePosition,
        top: mobileTop,
        left: 0,
        right: 0,
        bottom: 0,
        ...mobileMenu && {
          height: "100dvh",
          minHeight: "100dvh"
        }
      }
    }, children: /* @__PURE__ */ jsxs(Box, { role: "navigation", "aria-label": "Main Navigation", sx: {
      display: "flex",
      gap: "$spacing.sm",
      alignItems: "center",
      justifyContent: "space-between",
      padding: `${B2B_MAIN_NAV_PADDING_VERTICAL_PX}px`,
      margin: "0 auto",
      maxWidth: GROUP_WIDTH_MAP.xl,
      // Without this, when B2BLayout is transparent, there is no
      // white bg under the open mobile nav.
      ...mobileMenu && {
        background: "$colors.backgroundWhite"
      },
      "@media $lg_lte": {
        alignItems: "start",
        flexDirection: "column",
        gap: 0,
        padding: "$spacing.none",
        ...mobileMenu && {
          height: "100dvh",
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: "$spacing.xl"
        }
      }
    }, children: [
      logo && /* @__PURE__ */ jsxs(Box, { sx: {
        "@media $lg_lte": {
          display: "flex",
          alignItems: "center",
          justifyContent: "unset",
          width: "100%",
          padding: "$spacing.sm"
        },
        "@media $md_lte": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%"
        }
      }, children: [
        /* @__PURE__ */ jsx(Box, { sx: {
          "@media $lg_lte": {
            flex: 1
          }
        }, children: /* @__PURE__ */ jsx(LogoContainer, { href: logo.link, style: {
          display: "flex"
        }, children: /* @__PURE__ */ jsx(Box, { as: Image, src: logo.src, alt: logo.alt || "Logo", height: logo.height, width: logo.width, sx: {
          display: "block",
          maxHeight: `${B2B_MAIN_NAV_LOGO_MAX_HEIGHT_PX}px`,
          filter: transparent ? "saturate(0%) invert(100%) brightness(300%) contrast(100%)" : "none"
        } }) }) }),
        !disabled && /* @__PURE__ */ jsx(Box, { sx: {
          display: "none",
          "@media $lg_lte": {
            display: "block",
            marginRight: "$spacing.sm"
          },
          "@media $md_lte": {
            display: "none"
          }
        }, children: /* @__PURE__ */ jsxs(ButtonGroup, { "aria-label": "Calls to Action", children: [
          hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(Box, { as: TextButtonAnchor, sx: {
            ":hover, :focus": {
              textDecoration: transparent ? "underline !important" : "none !important",
              color: transparent ? "$colors.textWhite" : "$colors.blue500"
            },
            ":not(:hover)": {
              textDecoration: "none !important",
              color: transparent ? "$colors.textWhite" : "$colors.brand"
            }
          }, href: secondaryCTALink, onClick: yieldCallback(() => {
            handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
          }), children: secondaryCTALabel })),
          primaryCTALabel && primaryCTALink && /* @__PURE__ */ jsx(Button, { as: "a", href: primaryCTALink, buttonType: transparent ? "secondary" : "primary", onClick: yieldCallback(() => {
            handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
          }), children: primaryCTALabel })
        ] }) }),
        !disabled && /* @__PURE__ */ jsx(Box, { sx: {
          "@media $xl": {
            display: "none"
          }
        }, role: "button", "aria-expanded": mobileMenu, "aria-label": "Mobile Menu", onClick: () => setMobileMenu((prev) => !prev), children: /* @__PURE__ */ jsx(MobileMenuIcon, { size: "sm", fontColor: transparent ? "white" : "brand" }) })
      ] }),
      menu && !disabled && /* @__PURE__ */ jsx(MenuList, { sx: {
        display: "flex",
        flex: "auto",
        height: "100%",
        alignItems: "center",
        "@media $lg_lte": {
          display: mobileMenu ? "flex" : "none",
          flexDirection: "column",
          alignItems: "start",
          height: "auto",
          width: "100%",
          borderTop: "$borderWidths.xs solid $colors.borderLight"
        }
      }, children: menuItems.map((item) => /* @__PURE__ */ jsx(MenuItem, { item, transparent, sx: {
        display: "flex",
        alignItems: "end"
      } }, item.id)) }),
      (hasPrimaryCta || hasSecondaryCta) && !disabled && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Box, { sx: {
          "@media $lg_lte": {
            display: "none"
          }
        }, children: /* @__PURE__ */ jsxs(ButtonGroup, { "aria-label": "Call to Action buttons", children: [
          hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(Box, { as: TextButtonAnchor, sx: {
            ":hover, :focus": {
              textDecoration: transparent ? "underline !important" : "none !important"
            },
            ":not(:hover)": {
              textDecoration: "none !important",
              color: transparent ? "$colors.textWhite" : "$colors.brand"
            }
          }, inverse: transparent, href: secondaryCTALink, onClick: yieldCallback(() => {
            handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
          }), children: secondaryCTALabel })),
          hasPrimaryCta && /* @__PURE__ */ jsx(Button, { as: "a", href: primaryCTALink, buttonType: transparent ? "secondary" : "primary", onClick: yieldCallback(() => {
            handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
          }), children: primaryCTALabel })
        ] }) }),
        /* @__PURE__ */ jsx(Box, { sx: {
          display: "none",
          "@media $md_lte": {
            display: mobileMenu ? "block" : "none",
            padding: "$spacing.sm",
            marginTop: "$spacing.sm",
            width: "100%"
          }
        }, children: /* @__PURE__ */ jsxs(ButtonGroup, { fluid: true, "aria-label": "Call to Action buttons", direction: "column", children: [
          hasPrimaryCta && /* @__PURE__ */ jsx(Button, { as: "a", href: primaryCTALink, buttonType: "primary", onClick: yieldCallback(() => {
            handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
          }), children: primaryCTALabel }),
          hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(Button, { as: "a", href: secondaryCTALink, buttonType: "secondary", onClick: yieldCallback(() => {
            handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
          }), children: secondaryCTALabel }))
        ] }) })
      ] })
    ] }) })
  );
};
export {
  B2BMainNav as default
};
